import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { buildReleaseCounts } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { useDashboardDateRange, type DashboardRange } from '@/features/dashboard/hooks';
import {
  buildStatusMetricCards,
  buildStatusDeltaMap,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { queryReleaseSummary } from '@/features/releases/data';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import {
  queryKeys,
  useAccessToken,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ReleaseStatus } from '@flowqa/shared';

type UseReleasesPageViewModelResult = {
  range: DashboardRange;
  setRange: (value: DashboardRange) => void;
  selectedStatuses: ReleaseStatus[];
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  onStatusToggle: (status: ReleaseStatus) => void;
  onTagsChange: (nextTags: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  releaseMetricCards: ReturnType<typeof buildStatusMetricCards>;
  dateFiltersEnabled: boolean;
  showPageSkeleton: boolean;
};

export const useReleasesPageViewModel = (): UseReleasesPageViewModelResult => {
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [range, setRangeState] = useState<DashboardRange>('latest');
  const [selectedStatuses, setSelectedStatuses] = useState<ReleaseStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:releases');
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deltasEnabled = featureFlags?.deltas ?? true;
  const dateFiltersEnabled = featureFlags?.dateFilters ?? true;
  const showDelta = deltasEnabled && !isAllMode && previousRangeStartIso !== null;

  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const summaryFilters = useMemo(
    () => mergeFilters(buildDateRangeFilters('createdAt', rangeStartIso, rangeEndIso), tagFilters),
    [rangeEndIso, rangeStartIso, tagFilters],
  );
  const previousSummaryFilters = useMemo(
    () => buildDateRangeFilters('createdAt', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const releaseSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const previousReleaseSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: previousSummaryFilters,
      }),
    [previousSummaryFilters],
  );
  const releaseSummaryFiltersKey = useMemo(
    () => JSON.stringify(releaseSummaryInput.filters),
    [releaseSummaryInput.filters],
  );
  const previousReleaseSummaryFiltersKey = useMemo(
    () => JSON.stringify(previousReleaseSummaryInput.filters),
    [previousReleaseSummaryInput.filters],
  );
  const releaseSummaryQuery = useQuery({
    queryKey: queryKeys.releaseSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      releaseSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryReleaseSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        releaseSummaryInput,
        accessToken,
      ),
  });
  const previousReleaseSummaryQuery = useQuery({
    queryKey: queryKeys.releaseSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousReleaseSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryReleaseSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousReleaseSummaryInput,
        accessToken,
      ),
  });
  const releaseSummary = releaseSummaryQuery.data ?? null;
  const previousReleaseSummary = previousReleaseSummaryQuery.data ?? null;
  const releaseCounts = useMemo(() => buildReleaseCounts(releaseSummary), [releaseSummary]);
  const previousReleaseCounts = useMemo(
    () => buildReleaseCounts(previousReleaseSummary),
    [previousReleaseSummary],
  );
  const releaseDeltas = useMemo(
    () =>
      showDelta && previousReleaseSummary
        ? buildStatusDeltaMap(releaseCounts, previousReleaseCounts)
        : undefined,
    [previousReleaseCounts, previousReleaseSummary, releaseCounts, showDelta],
  );
  const releaseMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(releaseStatusOptions, resolveMetricTone),
    [],
  );
  const releaseMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: releaseMetricOptions,
        counts: releaseCounts,
        deltas: releaseDeltas,
        total: releaseSummary?.total,
        loading: releaseSummaryQuery.isLoading,
        showDelta,
      }),
    [
      releaseCounts,
      releaseDeltas,
      releaseMetricOptions,
      releaseSummary?.total,
      releaseSummaryQuery.isLoading,
      showDelta,
    ],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const tableFilters = useMemo(
    () => mergeFilters(summaryFilters, statusFilters, tagFilters),
    [statusFilters, summaryFilters, tagFilters],
  );
  const onStatusToggle = useCallback((status: ReleaseStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);

  const showPageSkeleton = Boolean(accessToken) && releaseSummaryQuery.isLoading && !releaseSummary;

  const setRange = useCallback((value: DashboardRange) => {
    setRangeState(value);
  }, []);

  return {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    onStatusToggle,
    onTagsChange: setSelectedTags,
    tableFilters,
    releaseMetricCards,
    dateFiltersEnabled,
    showPageSkeleton,
  };
};
